import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submit"]

  connect() {
    this.element.setAttribute("novalidate", true)
    this.element.addEventListener("blur", this.validateField.bind(this), true)
    this.element.addEventListener("input", this.handleInput.bind(this), true)
    this.element.addEventListener("submit", this.submit.bind(this))
  }

  validateField(event) {
    const field = event.target
    if (!field.willValidate) return

    if (!field.checkValidity()) {
      this.showError(field)
    }
  }

  handleInput(event) {
    const field = event.target
    if (!field.willValidate) return

    if (field.checkValidity()) {
      this.clearError(field)
      this.showSuccess(field)
    } else if (field.classList.contains("input-error")) {
      this.showError(field)
    }
  }

  clearError(field) {
    const wrapper = this.getFieldWrapper(field)
    const errorElement = wrapper.querySelector(".field-error")
    
    if (errorElement) {
      errorElement.style.opacity = "0"
      errorElement.style.transform = "translateY(-8px)"
      setTimeout(() => errorElement.remove(), 200)
    }
    
    field.classList.remove("input-error", "input-shake")
  }

  showError(field) {
    field.classList.add("input-error")
    field.classList.remove("input-success")
    
    this.removeSuccessIcon(field)

    if (!field.classList.contains("input-shake")) {
      field.classList.add("input-shake")
      setTimeout(() => field.classList.remove("input-shake"), 500)
    }

    const wrapper = this.getFieldWrapper(field)
    let errorElement = wrapper.querySelector(".field-error")
    
    if (!errorElement) {
      errorElement = document.createElement("p")
      errorElement.className = "field-error"
      errorElement.setAttribute("role", "alert")
      errorElement.setAttribute("aria-live", "polite")
      wrapper.appendChild(errorElement)
    }

    errorElement.textContent = this.getErrorMessage(field)
  }

  showSuccess(field) {
    field.classList.remove("input-error")
    field.classList.add("input-success")
    
    this.removeSuccessIcon(field)
    
    const wrapper = this.getFieldWrapper(field)
    if (wrapper.style.position !== "relative") {
      wrapper.style.position = "relative"
    }
    
    const successIcon = document.createElement("span")
    successIcon.className = "field-success-icon"
    successIcon.setAttribute("aria-hidden", "true")
    successIcon.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
    `
    
    const inputWrapper = field.parentElement
    if (inputWrapper.classList.contains("relative")) {
      inputWrapper.appendChild(successIcon)
    } else {
      const relativeWrapper = document.createElement("div")
      relativeWrapper.className = "relative"
      field.parentNode.insertBefore(relativeWrapper, field)
      relativeWrapper.appendChild(field)
      relativeWrapper.appendChild(successIcon)
    }
  }

  removeSuccessIcon(field) {
    const wrapper = this.getFieldWrapper(field)
    const existingIcon = wrapper.querySelector(".field-success-icon")
    if (existingIcon) {
      existingIcon.remove()
    }
  }

  getFieldWrapper(field) {
    const formGroup = field.closest(".form-group")
    return formGroup || field.parentNode
  }

  getErrorMessage(field) {
    if (field.validity.valueMissing) {
      return field.dataset.requiredMessage || "This field is required"
    }
    if (field.validity.typeMismatch) {
      if (field.type === "email") {
        return "Please enter a valid email address"
      }
      if (field.type === "url") {
        return "Please enter a valid URL"
      }
    }
    if (field.validity.tooShort) {
      return `Must be at least ${field.minLength} characters`
    }
    if (field.validity.tooLong) {
      return `Must be no more than ${field.maxLength} characters`
    }
    if (field.validity.patternMismatch) {
      return field.dataset.patternMessage || "Please match the requested format"
    }
    if (field.validity.rangeUnderflow) {
      return `Value must be at least ${field.min}`
    }
    if (field.validity.rangeOverflow) {
      return `Value must be no more than ${field.max}`
    }
    return field.validationMessage
  }

  submit(event) {
    const invalidFields = this.element.querySelectorAll(":invalid")
    
    if (invalidFields.length > 0) {
      event.preventDefault()
      
      invalidFields.forEach((field, index) => {
        setTimeout(() => this.showError(field), index * 50)
      })
      
      invalidFields[0].focus()
      invalidFields[0].scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }
}
